(function () {
  'use strict';

  angular.module('wsLrs.services').
  /**
   * A place for general utility functions.
   */
  factory('ListUtils', function () {
    /**
     * LinkedLists
     *
     * This is a light-weight LinkedList implementation[en:1], defined as follows:
     *  - Nil (LNil) is a LinkedList
     *  - a Singleton (LAtom) is a LinkedList
     *  - a Cons Cell (LPair) is a LinkedList
     *
     * A LinkedList is represented by plain a Javascript Object with the following interpretation:
     *  - an Object with the properties `value` and `next` is an LPair
     *  - an Object with the property `value` but not `next` is an LAtom
     *  - any other Object is an LNil[en:2]
     *
     * note: each LinkedList method has a final, optional config-object with the following properties:
     *  | key | description                                 | default value | optional |
     *  |-----+---------------------------------------------+---------------+----------|
     *  | a   | the name of the value of a linked-list node | 'value'       | yes      |
     *  | d   | the name of a link to another node          | 'next'        |          |
     *
     * More complete implementations of linked lists might instead have a distinguished Nil object that terminates
     * all instances.  Taking this structural approach, maintains some flexibility.  If you're applying these to
     * some existing data that already has a linked-list structure, you don't have have to create terminal objects;
     * the linked list methods will terminate on a final list element or a terminal object with no (relevant)
     * properties.  Likewise if you're creating the data yourself, you should be able to use plain linked lists
     * and non-empty linked lists interchangeably.  It's up to the user to decide which approach fits.
     *
     * instantiate LinkedList with the following:
     *  const L = instantiateLinkedLists()
     *
     * end notes:
     * [en:1] in fact this is more of a mix-in
     * [en:2] LinkedLists methods can't distinguish between objects that have neither a `value` nor a `next`
     * property.  in particular, the user should not assume anything about an LNil, other than it is an LNil
     *
     */
    var LinkedLists = function () {
      // todo add new methods
      var ll = {};

      // TODO isLNil: Object -> Boolean
      // TODO isLAtom: Object -> Boolean
      // TODO isLPair: Object -> Boolean

      // TODO reduce: List(x) -> ((a, x) -> a) -> a -> a

      // ll_to_array: List(x) -> [x]
      function ll_to_array(xs) {
        var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          _ref$a = _ref.a,
          a = _ref$a === void 0 ? "value" : _ref$a,
          _ref$d = _ref.d,
          d = _ref$d === void 0 ? "next" : _ref$d;
        var arr = [];
        forEach(xs, function (x) {
          return arr.push(x);
        }, {
          a: a,
          d: d
        });
        return arr;
      }
      ll.ll_to_array = ll_to_array;

      // array_to_ll: [x] -> List(x)
      function array_to_ll(xs) {
        var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          _ref2$a = _ref2.a,
          a = _ref2$a === void 0 ? "value" : _ref2$a,
          _ref2$d = _ref2.d,
          d = _ref2$d === void 0 ? "next" : _ref2$d;
        var result = {};
        var last = result;
        xs.forEach(function (x) {
          last[d] = {};
          last[d][a] = x;
          last = last[d];
        });
        return result;
      }
      ll.array_to_ll = array_to_ll;

      // todo: re-implement forEach, map, etc, with reduce
      // reduce: List(x) -> y -> (x -> y -> y) -> y
      function reduce(xs, y0, f) {
        var _ref3 = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
          _ref3$a = _ref3.a,
          a = _ref3$a === void 0 ? "value" : _ref3$a,
          _ref3$d = _ref3.d,
          d = _ref3$d === void 0 ? "next" : _ref3$d;
        // 1) when xs is empty, do nothing
        if (xs[a] === undefined && xs[d] === undefined) {
          return y0;
        }
        // 2) do something with the first element of xs
        var y = f(xs[a], y0);
        // 3) if xs contains only one element don't do any further recursion
        if (xs[d] === undefined) {
          return y;
        }
        // 4) recur on the remaining elements of xs
        return reduce(xs[d], y, f, {
          a: a,
          d: d
        });
      }
      ll.reduce = reduce;

      // forEach: List(x) -> (x -> ()) -> ()
      function forEach(xs, f) {
        var _ref4 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          _ref4$a = _ref4.a,
          a = _ref4$a === void 0 ? "value" : _ref4$a,
          _ref4$d = _ref4.d,
          d = _ref4$d === void 0 ? "next" : _ref4$d;
        // 1) when xs is empty, do nothing
        if (xs[a] === undefined && xs[d] === undefined) {
          return;
        }
        // 2) do something with the first element of xs
        f(xs[a]);
        // 3) if xs contains only one element don't do any further recursion
        if (xs[d] === undefined) {
          return;
        }
        // 4) recur on the remaining element of xs
        forEach(xs[d], f, {
          a: a,
          d: d
        });
      }
      ll.forEach = forEach;

      // seek: List(x) -> (x -> Boolean) -> List(x)?
      function seek(xs, f) {
        var _ref5 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          _ref5$a = _ref5.a,
          a = _ref5$a === void 0 ? "value" : _ref5$a,
          _ref5$d = _ref5.d,
          d = _ref5$d === void 0 ? "next" : _ref5$d;
        if (xs[a] === undefined) {
          return;
        }
        if (f(xs[a])) {
          return xs;
        }
        if (xs[d] === undefined) {
          return;
        }
        return seek(xs[d], f, {
          a: a,
          d: d
        });
      }
      ll.seek = seek;

      // find: List(x) -> (x -> Boolean) -> x?
      function find(xs, f) {
        var _ref6 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          _ref6$a = _ref6.a,
          a = _ref6$a === void 0 ? "value" : _ref6$a,
          _ref6$d = _ref6.d,
          d = _ref6$d === void 0 ? "next" : _ref6$d;
        var pos = seek(xs, f, {
          a: a,
          d: d
        });
        if (pos !== undefined) {
          return pos[a];
        }
      }
      ll.find = find;

      // filter: List(x) -> (x -> Boolean) -> List(x)
      function filter(xs, f) {
        var _ref7 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          _ref7$a = _ref7.a,
          a = _ref7$a === void 0 ? "value" : _ref7$a,
          _ref7$d = _ref7.d,
          d = _ref7$d === void 0 ? "next" : _ref7$d;
        function _filter(xs) {
          if (xs === undefined) {
            return xs;
          }
          if (xs[a] === undefined && xs[d] === undefined) {
            return xs;
          }
          var matchFirst = f(xs[a]);
          var rest = _filter(xs[d]);
          if (!matchFirst) {
            return rest;
          }
          xs[d] = rest;
          return xs;
        }
        var head = find(xs, f, {
          a: a,
          d: d
        });
        return _filter(head);
      }
      ll.filter = filter;

      // map: List(x) -> (x -> y) -> List(y)
      function map(xs, f) {
        var _ref8 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          _ref8$a = _ref8.a,
          a = _ref8$a === void 0 ? "value" : _ref8$a,
          _ref8$d = _ref8.d,
          d = _ref8$d === void 0 ? "next" : _ref8$d;
        // 1) when xs is empty, do nothing
        if (xs[a] === undefined && xs[d] === undefined) {
          return {};
        }
        // 2) do something with the first element of xs
        var result = {};
        result[a] = f(xs[a]);
        // 3) if xs contains only one element don't do any further recursion
        if (xs[d] === undefined) {
          return result;
        }
        // 4) recur on the remaining element of xs
        result[d] = map(xs[d], f, {
          a: a,
          d: d
        });
        return result;
      }
      ll.map = map;

      /**
       * add an element to the end of a linked list.  this may mutate
       * the given list
       *
       * @param {LinkedList(X)} xs
       * @param {X} x
       * @param {{a: String, d: String}} mapping
       * @returns
       */
      function push(xs, x) {
        var _ref9 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          _ref9$a = _ref9.a,
          a = _ref9$a === void 0 ? "value" : _ref9$a,
          _ref9$d = _ref9.d,
          d = _ref9$d === void 0 ? "next" : _ref9$d;
        function _push(xs) {
          // ignore empty lists
          if (xs === undefined) {
            return;
          }
          // add a value to an empty list
          if (xs[a] === undefined && xs[d] === undefined) {
            xs[a] = x;
            return xs;
          }
          // add a singleton to a singleton
          if (xs[d] === undefined) {
            xs[d] = {};
            xs[d][a] = x;
            return xs[d];
          }
          // keep looking for the end of the list
          return _push(xs[d]);
        }
        return _push(xs);
      }
      ll.push = push;

      /**
       * flatten: List(List(x)) -> List(x)
       * _Note_ this is equivalent to concatenating an array of arrays,
       * i.e. flatten(xs) assumes each element of xs is a List and
       * merges all the elements of those top-level Lists into a single
       * List.  If you want to completly flatten an array, you may
       * invoke flatten repeatedly
       *
       * @param {List(List(x))} xs
       * @returns a List(x)
       */
      function flatten(xs) {
        var _ref0 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          _ref0$a = _ref0.a,
          a = _ref0$a === void 0 ? "value" : _ref0$a,
          _ref0$d = _ref0.d,
          d = _ref0$d === void 0 ? "next" : _ref0$d;
        if (xs === undefined) {
          return;
        }
        var result = {};
        var last = result;
        function splat(xs) {
          if (xs[a] === undefined) {
            return;
          }
          forEach(xs[a], function (x) {
            return last = push(last, x);
          }, {
            a: a,
            d: d
          });
          if (xs[d] !== undefined) {
            splat(xs[d]);
          }
        }
        splat(xs);
        return result;
      }
      ll.flatten = flatten;
      return Object.freeze(ll);
    }();

    /**
     * AssociationLists
     *
     * an AssociationList (AList) is a LinkedList of objects containing a 'key' and a
     * 'value'.  This is best used as a lightweight key-value store.  If you need store a lot of values
     * and get and set at high speeds, then you'll want a different data structure.
     *
     *  - the 'insert' method puts a new key-value pair (kvp) at the start of a list
     *  - the 'lookup', 'find', and 'seek' methods stop searching wheh then they find a match
     *  - the 'remove' method removes all matching kvps
     *
     *  note: since insert simply adds a new kvp to the head of an a-list, and lookup, find, and seek
     *   take the first match they find, you can keep your key-value store's history by just not using
     *   remove (you'll need to implement a remove-first method or remove-first-n method to actually
     *   access the history, but that should be trivial to implement
     *
     *   an AList is just a LinkedList({key: Key, val: Value})
     *
     *   insert:    Alist -> Key -> Value -> AList
     *   lookup:    Alist -> Key -> Value
     *   find:      Alist -> Key -> {key: Key, val: Value}
     *   lookup:    Alist -> Key -> AList
     *
     */
    var ALists = function () {
      var al = {};
      /**
       * organize a flat array into an array of key-value pairs, called
       * an [association list](https://en.wikipedia.org/wiki/Associative_array)
       *
       * @param {[x]} xs
       * @param {*} k
       * @returns
       */
      function array_to_alist(xs) {
        var _ref1 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          _ref1$a = _ref1.a,
          a = _ref1$a === void 0 ? "kv_pair" : _ref1$a,
          _ref1$d = _ref1.d,
          d = _ref1$d === void 0 ? "next" : _ref1$d;
        if (xs.length % 2 !== 0) {
          return;
        }
        var acc = {};
        var i = xs.length - 2;
        while (i >= 0) {
          acc = insert(acc, xs[i], xs[i + 1], {
            a: a,
            d: d
          });
          i = i - 2;
        }
        return acc;
      }
      al.array_to_alist = array_to_alist;
      function insert(a_list, key, val) {
        var _ref10 = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
          _ref10$a = _ref10.a,
          a = _ref10$a === void 0 ? "kv_pair" : _ref10$a,
          _ref10$d = _ref10.d,
          d = _ref10$d === void 0 ? "next" : _ref10$d;
        var result = {};
        result[a] = {
          key: key,
          val: val
        };
        result[d] = a_list;
        return result;
      }
      al.insert = insert;
      function lookup(a_list, key) {
        var _ref11 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          _ref11$a = _ref11.a,
          a = _ref11$a === void 0 ? "kv_pair" : _ref11$a,
          _ref11$d = _ref11.d,
          d = _ref11$d === void 0 ? "next" : _ref11$d,
          alt = _ref11.alt;
        var assoc = find(a_list, key, {
          a: a,
          d: d,
          alt: {
            key: key,
            val: alt
          }
        });
        return assoc.val;
      }
      al.lookup = lookup;
      function seek(a_list, f) {
        var _ref12 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          _ref12$a = _ref12.a,
          a = _ref12$a === void 0 ? "kv_pair" : _ref12$a,
          _ref12$d = _ref12.d,
          d = _ref12$d === void 0 ? "next" : _ref12$d;
        if (typeof f === 'string') {
          return seek(a_list, function (_ref13) {
            var key = _ref13.key;
            return key === f;
          }, {
            a: a,
            d: d
          });
        }
        if (Array.isArray(f)) {
          return seek(a_list, function (_ref14) {
            var key = _ref14.key;
            return f.includes(key);
          }, {
            a: a,
            d: d
          });
        }
        return LinkedLists.seek(a_list, f, {
          a: a,
          d: d
        });
      }
      al.seek = seek;
      function find(a_list, f) {
        var _ref15 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          _ref15$a = _ref15.a,
          a = _ref15$a === void 0 ? "kv_pair" : _ref15$a,
          _ref15$d = _ref15.d,
          d = _ref15$d === void 0 ? "next" : _ref15$d,
          alt = _ref15.alt;
        var node = seek(a_list, f, {
          a: a,
          d: d
        });
        if (node === undefined || node[a].val === undefined) {
          return alt;
        }
        return node[a];
      }
      al.find = find;
      function remove(a_list, k) {
        var _ref16 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
          _ref16$a = _ref16.a,
          a = _ref16$a === void 0 ? "kv_pair" : _ref16$a,
          _ref16$d = _ref16.d,
          d = _ref16$d === void 0 ? "next" : _ref16$d;
        return LinkedLists.filter(a_list, function (_ref17) {
          var key = _ref17.key;
          return key !== k;
        }, {
          a: a,
          d: d
        });
      }
      al.remove = remove;
      return Object.freeze(al);
    }();
    return {
      LinkedLists: LinkedLists,
      ALists: ALists
    };
  });
})();
